import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'

// Redirect component for legacy production edit URLs
function ProductionEditRedirect() {
  const { id } = useParams()
  return <Navigate to={`/admin/products/${id}/edit`} replace />
}

// Layouts
import { PublicLayout } from '@/components/layout/PublicLayout'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AccountLayout } from '@/components/layout/AccountLayout'
import { ProtectedRoute } from '@/components/ProtectedRoute'

// Public pages
import { HomePage } from '@/pages/HomePage'
import { ArchivePage } from '@/pages/ArchivePage'
import { ProductPage } from '@/pages/ProductPage'
import { CartPage } from '@/pages/CartPage'
import { AboutPage } from '@/pages/AboutPage'
import { ContactPage } from '@/pages/ContactPage'
import { OrderSuccessPage } from '@/pages/OrderSuccessPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

// Auth pages
import { AuthCallbackPage } from '@/pages/auth/AuthCallbackPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { ConfirmPage } from '@/pages/auth/ConfirmPage'

// Account pages
import { AccountDashboard } from '@/pages/account/AccountDashboard'
import { AccountOrdersPage } from '@/pages/account/AccountOrdersPage'
import { AccountOrderDetailPage } from '@/pages/account/AccountOrderDetailPage'
import { AccountProfilePage } from '@/pages/account/AccountProfilePage'
import { AccountSettingsPage } from '@/pages/account/AccountSettingsPage'

// Admin pages
import { AdminDashboard } from '@/pages/admin/AdminDashboard'
import { AdminDropsPage } from '@/pages/admin/AdminDropsPage'
import { AdminOrdersPage } from '@/pages/admin/AdminOrdersPage'
import { AdminOrderDetailPage } from '@/pages/admin/AdminOrderDetailPage'
import { AdminProductsPage } from '@/pages/admin/AdminProductsPage'
import { AdminProductNewPage } from '@/pages/admin/AdminProductNewPage'
import { AdminProductEditPage } from '@/pages/admin/AdminProductEditPage'
import { AdminTeamPage } from '@/pages/admin/AdminTeamPage'
import { AdminTeamInvitePage } from '@/pages/admin/AdminTeamInvitePage'
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage'

function App() {
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/past-reviews" element={<ArchivePage />} />
          <Route path="/archive" element={<Navigate to="/past-reviews" replace />} />
          <Route path="/cart" element={<CartPage />} />
          {/* Product detail page - supports both new and legacy paths */}
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/production/:slug" element={<ProductPage />} />
          <Route path="/review/:slug" element={<ProductPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/order/success" element={<OrderSuccessPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Auth routes (no layout) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/confirm" element={<ConfirmPage />} />

        {/* Protected: Customer account */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AccountLayout />}>
            <Route path="/account" element={<AccountDashboard />} />
            <Route path="/account/orders" element={<AccountOrdersPage />} />
            <Route path="/account/orders/:id" element={<AccountOrderDetailPage />} />
            <Route path="/account/profile" element={<AccountProfilePage />} />
            <Route path="/account/settings" element={<AccountSettingsPage />} />
          </Route>
        </Route>

        {/* Protected: Admin */}
        <Route element={<ProtectedRoute requiredRole="editor" />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/drops" element={<AdminDropsPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/orders/:id" element={<AdminOrderDetailPage />} />
            {/* Product management - new routes */}
            <Route path="/admin/products" element={<AdminProductsPage />} />
            <Route path="/admin/products/new" element={<AdminProductNewPage />} />
            <Route path="/admin/products/:id/edit" element={<AdminProductEditPage />} />
            {/* Legacy route redirects for backward compatibility */}
            <Route path="/admin/productions" element={<Navigate to="/admin/products" replace />} />
            <Route path="/admin/productions/new" element={<Navigate to="/admin/products/new" replace />} />
            <Route path="/admin/productions/:id/edit" element={<ProductionEditRedirect />} />
            <Route path="/admin/team" element={<AdminTeamPage />} />
            <Route path="/admin/team/invite" element={<AdminTeamInvitePage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>
        </Route>
      </Routes>
      <Toaster />
    </>
  )
}

export default App
